import React, { useEffect, useState } from "react";
import { Select } from "@mantine/core";
import "../Admin/Event/AdminEvent.css";

interface PersonDto {
    personId: number;
    firstName?: string;
    lastName?: string;
    fullName?: string;
}

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface DrinkStatusDto {
    functionDrinks: number;
    personalDrinks: number;
    totalAllowed: number;
    usedDrinks: number;
    remainingDrinks: number;
}

const AdminPersonExtraDrinks: React.FC = () => {
    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);
    const [eventName, setEventName] = useState<string>("");

    const [persons, setPersons] = useState<PersonDto[]>([]);
    const [personId, setPersonId] = useState<number>(0);

    const [drinkStatus, setDrinkStatus] = useState<DrinkStatusDto | null>(null);

    const [maxDrinks, setMaxDrinks] = useState<number>(0);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // ---------- Helper ----------
    const getPersonLabel = (p: PersonDto): string => {
        if (p.fullName && p.fullName.trim().length > 0)
            return p.fullName.trim();

        const combined = `${p.firstName || ""} ${p.lastName || ""}`.trim();
        return combined || `Person ${p.personId}`;
    };

    // ---------------- HENT EVENTS ----------------
    useEffect(() => {
        fetch("/api/events")
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setEvents(data);
                }
            })
            .catch(() => setError("Kunne ikke hente events"));
    }, []);

    // ---------------- EVENT CHANGE ----------------
    const handleEventChange = async (id: number) => {
        setMessage(null);
        setError(null);
        setEventId(id);
        setPersonId(0);
        setDrinkStatus(null);
        setPersons([]);

        if (!id) {
            setEventName("");
            return;
        }

        const selected = events.find(e => e.eventID === id);
        setEventName(selected?.eventName || `Event ${id}`);

        try {
            const res = await fetch(`/api/events/${id}/persons`);
            if (!res.ok) throw new Error("Kunne ikke hente personer");

            const data = await res.json();
            setPersons(Array.isArray(data) ? data : []);
        } catch (err: any) {
            setError(err.message);
        }
    };

    // ---------------- PERSON CHANGE ----------------
    const handlePersonChange = (value: string | null) => {
        setMessage(null);
        setError(null);
        setMaxDrinks(0);

        const id = value ? Number(value) : 0;
        setPersonId(id);
    };

    // ---------------- HENT STATUS ----------------
    useEffect(() => {
        if (!personId || !eventId) {
            setDrinkStatus(null);
            return;
        }

        fetch(`/api/drinks/status?personId=${personId}&eventId=${eventId}`)
            .then(r => r.ok ? r.json() : null)
            .then((data: DrinkStatusDto) => {
                if (data) setDrinkStatus(data);
            });
    }, [personId, eventId]);

    // ---------------- SUBMIT ----------------
    const submit = async () => {
        setMessage(null);
        setError(null);

        if (!eventId) return setError("Event skal vælges");
        if (!personId) return setError("Person skal vælges");
        if (maxDrinks === 0) return setError("Indtast et tal");

        try {
            const res = await fetch("/api/admin/drinks-person", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    personId,
                    eventId,
                    maxDrinks
                }),
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text);
            }

            setMessage(
                maxDrinks > 0
                    ? `✅ ${maxDrinks} drinks tilføjet`
                    : `➖ ${Math.abs(maxDrinks)} drinks fjernet`
            );

            setMaxDrinks(0);

            // Refresh status efter opdatering
            const statusRes = await fetch(
                `/api/drinks/status?personId=${personId}&eventId=${eventId}`
            );

            if (statusRes.ok) {
                const updatedStatus = await statusRes.json();
                setDrinkStatus(updatedStatus);
            }

        } catch (err: any) {
            setError(err.message);
        }
    };

    return (
        <div className="form-container">
            <h2>
                {eventName || "Vælg event"}
                {personId ? " – Person → Juster drinks" : ""}
            </h2>

            {error && <p className="error-message">{error}</p>}
            {message && <p className="success-message">{message}</p>}

            {/* EVENT DROPDOWN */}
            <label>Event *</label>
            <select
                value={eventId}
                onChange={(e) => handleEventChange(Number(e.target.value))}
            >
                <option value={0}>-- Vælg event --</option>
                {events.map(e => (
                    <option key={e.eventID} value={e.eventID}>
                        {e.eventName || `Event ${e.eventID}`}
                    </option>
                ))}
            </select>

            {/* PERSON SØGBAR DROPDOWN */}
            {eventId > 0 && (
                <>
                    <label>Person *</label>

                    <Select
                        searchable
                        clearable
                        placeholder="Søg person..."
                        value={personId ? String(personId) : null}
                        onChange={handlePersonChange}
                        data={persons.map((p) => ({
                            value: String(p.personId),
                            label: getPersonLabel(p),
                        }))}
                        nothingFoundMessage="Ingen personer fundet"
                    />
                </>
            )}

            {/* STATUS */}
            {drinkStatus && (
                <div style={{ margin: "15px 0" }}>
                    <strong>Status:</strong>

                    <div>Fra function: {drinkStatus.functionDrinks}</div>
                    <div>Ekstra (person): {drinkStatus.personalDrinks}</div>

                    <hr style={{ margin: "8px 0" }} />

                    <div>Total tilladt: {drinkStatus.totalAllowed}</div>
                    <div>Brugt: {drinkStatus.usedDrinks}</div>

                    <div
                        style={{
                            fontWeight: "bold",
                            color:
                                drinkStatus.remainingDrinks <= 0
                                    ? "red"
                                    : "green"
                        }}
                    >
                        Tilbage: {drinkStatus.remainingDrinks}
                    </div>
                </div>
            )}

            {/* JUSTER DRINKS */}
            {personId > 0 && (
                <>
                    <label>Juster drinks (brug minus for at fjerne)</label>
                    <input
                        type="number"
                        value={maxDrinks}
                        onChange={(e) => setMaxDrinks(Number(e.target.value))}
                    />

                    <button className="add-btn" onClick={submit}>
                        Opdater
                    </button>
                </>
            )}
        </div>
    );
};

export default AdminPersonExtraDrinks;
